/**
 * useReceivableOrders Hook
 *
 * Fetches purchase orders that can receive goods receipts.
 * Only orders with status SENT or PARTIALLY_RECEIVED are returned.
 */

import { useQuery } from '@tanstack/react-query';
import { purchaseOrdersService } from '@/features/purchase-orders/services/purchase-orders.service';
import { PurchaseOrderStatus } from '@bstock/shared';
import type { PurchaseOrderListItem } from '@bstock/shared';

/**
 * Query key for receivable orders
 */
export const receivableOrdersKeys = {
  all: ['receivable-orders'] as const,
  list: () => [...receivableOrdersKeys.all, 'list'] as const,
};

/**
 * Fetches all orders that can be received (SENT or PARTIALLY_RECEIVED).
 * Uses a single API call with comma-separated statuses.
 *
 * @param enabled - Whether to enable the query (useful for modals)
 */
export function useReceivableOrders(enabled: boolean = true) {
  return useQuery({
    queryKey: receivableOrdersKeys.list(),
    queryFn: async (): Promise<PurchaseOrderListItem[]> => {
      const response = await purchaseOrdersService.list({
        status: [PurchaseOrderStatus.SENT, PurchaseOrderStatus.PARTIALLY_RECEIVED].join(','),
        limit: 100,
        sortBy: 'orderDate',
        sortOrder: 'desc',
      });

      return response.data;
    },
    staleTime: 30 * 1000, // 30 seconds - orders don't change frequently
    enabled,
  });
}
